import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { LaneCatalogue, LaneCustomer, LaneDetail, LaneEvidence, LaneIdentity, LaneLocationRole, LaneRow, LaneResolution, LaneSave, LaneSettings, LaneSitesResponse } from './lane.models';
@Injectable({ providedIn: 'root' })
export class LaneRoutingService {
    private readonly endpoint = '/api/managers/lane_routing.php';
    constructor(private readonly http: HttpClient) { }
    private get<T>(action: string, query: Record<string, string> = {}): Observable<T> {
        return this.http.get<T>(this.endpoint, { params: new HttpParams({ fromObject: { action, ...query } }) });
    }
    catalogue(): Observable<LaneCatalogue> { return this.get('catalogue'); }
    list(q: string, offset = 0): Observable<{
        data: LaneRow[];
        total: number;
        has_more: boolean;
    }> { return this.get('list', { q, offset: String(offset) }); }
    customers(q: string, mode: 'popular' | 'search' = 'search'): Observable<{
        data: LaneCustomer[];
    }> { return this.get('customers', { q, mode }); }
    popularCustomers(): Observable<{
        data: LaneCustomer[];
    }> { return this.customers('', 'popular'); }
    sites(
        q: string,
        customer?: string,
        role?: LaneLocationRole,
        mode: 'known' | 'all' = 'known',
        offset = 0,
        pickup?: LaneIdentity['shipper'] | null,
        consignee?: LaneIdentity['consignee'] | null,
        date = ''
    ): Observable<LaneSitesResponse> {
        const query: Record<string, string> = { q };
        if (customer && role) {
            query['customer'] = customer;
            query['role'] = role;
            query['mode'] = mode;
            query['offset'] = String(offset);
            if (date) query['date'] = date;
            if (pickup) {
                query['pickup_contact'] = pickup.contact_uuid;
                query['pickup_address'] = pickup.address_uuid;
            }
            if (consignee) {
                query['consignee_contact'] = consignee.contact_uuid;
                query['consignee_address'] = consignee.address_uuid;
            }
        }
        return this.get('sites', query);
    }
    detail(uuid: string): Observable<LaneDetail> { return this.get('detail', { uuid }); }
    resolve(body: LaneIdentity): Observable<LaneResolution> { return this.http.post<LaneResolution>(this.endpoint, body, { params: { action: 'resolve' } }); }
    history(body: LaneIdentity): Observable<LaneEvidence> { return this.http.post<LaneEvidence>(this.endpoint, body, { params: { action: 'history' } }); }
    save(body: LaneSave, uuid?: string): Observable<LaneDetail> {
        return uuid ? this.http.put<LaneDetail>(this.endpoint, body, { params: { action: 'update', uuid } })
            : this.http.post<LaneDetail>(this.endpoint, body, { params: { action: 'create' } });
    }
    approve(uuid: string, version: number): Observable<LaneDetail> { return this.http.post<LaneDetail>(this.endpoint, { expected_version: version }, { params: { action: 'approve', uuid } }); }
    enabled(uuid: string, version: number, enabled: boolean): Observable<LaneDetail> { return this.http.put<LaneDetail>(this.endpoint, { expected_version: version, enabled }, { params: { action: 'enabled', uuid } }); }
    settings(body: LaneSettings): Observable<LaneCatalogue> {
        return this.http.put<LaneCatalogue>(this.endpoint, { sample_size: body.sample_size, lookback_days: body.lookback_days, minimum_agreement: body.minimum_agreement, completed_statuses: body.completed_statuses, expected_version: body.version }, { params: { action: 'settings' } });
    }
}
